/**
 * The shared vocabulary for song licences — what a church is actually
 * agreeing to when a songwriter endorses a song for use.
 *
 * A licence is not the same thing as reach (app/src/lib/reach.js — who can
 * see a song) and not the same thing as songs.destination (which queue a
 * song was submitted to). This is the third, separate concept: named
 * permission to actually use a specific song, for a specific church, on
 * terms both sides agreed to.
 *
 * Copyright never moves. Nothing here is an assignment, a transfer of
 * ownership, or work for hire — it's an endorsement plus a licence, and the
 * words above should never appear in copy built from this file.
 *
 * Mirrors song_licences in migration 34. If you add an option here, add the
 * matching CHECK constraint there, and nowhere else.
 */

export const SCOPES = [
  {
    id: 'rehearsal',
    column: 'scope_rehearsal',
    label: 'Rehearsal',
    blurb: 'Practising it together, without playing it in front of the congregation.',
  },
  {
    id: 'live',
    column: 'scope_live',
    label: 'Live in a service',
    blurb: 'Playing it live, in person, during a service or church event.',
  },
  {
    id: 'recording',
    column: 'scope_recording',
    label: 'Recording',
    blurb: 'Recording a version of it, for the church’s own use.',
  },
  {
    id: 'streaming',
    column: 'scope_streaming',
    label: 'Streaming',
    blurb: 'Streaming or posting a recording of it online.',
  },
  {
    id: 'print',
    column: 'scope_print',
    label: 'Print & lyrics display',
    blurb: 'Printing the words or chords, or putting them on a screen.',
  },
]

/**
 * The plain-English grouping of SCOPES a pastor actually decides on — the
 * two tiers described in the legal brief (Secret Place Music, request for
 * advice, 17 Aug 2026): "Approved for Congregational Use" and "Approved for
 * External Use". A pastor is being asked to sign off on a licence, not to
 * pick five checkboxes cold — so the decision is framed at the tier a
 * non-lawyer actually reasons in, with the underlying scopes shown as detail
 * underneath for anyone who wants the weeds.
 *
 * Purely a UI grouping over the same scope_* columns SCOPES already maps to
 * — no new column, no new vocabulary the database has to know about. If a
 * tier's scope list changes, it still has to change in SCOPES and in the
 * database CHECK constraints per the note above.
 */
export const TIERS = [
  {
    id: 'congregational',
    label: 'Approved for Congregational Use',
    short: 'Congregational Use',
    scopeIds: ['rehearsal', 'live', 'print'],
    blurb: 'The everyday, in-house use: rehearsing it, playing it live in a service, and printing or projecting the words and chords.',
  },
  {
    id: 'external',
    label: 'Approved for External Use',
    short: 'External Use',
    scopeIds: ['recording', 'streaming'],
    blurb: 'Taking it beyond the room: recording a version, and streaming or posting a recording of it online.',
    excludes: 'This never includes changing the lyrics, releasing it commercially, or licensing it to anyone else — those stay separate, and always need the songwriter’s direct say-so.',
  },
]

/** Whether every scope in a tier is currently granted on a licence row. */
export const tierGranted = (row, tier) =>
  tier.scopeIds.every((sid) => row[SCOPES.find((s) => s.id === sid).column])

export const DURATIONS = [
  {
    id: '6_months',
    months: 6,
    label: '6 months',
    blurb: 'A short trial — the licence needs agreeing again after half a year.',
  },
  {
    id: '1_year',
    months: 12,
    label: '1 year',
    blurb: 'A full year of use before it needs agreeing again.',
  },
  {
    id: 'indefinite',
    months: null,
    label: 'Indefinite, until revoked',
    blurb: 'No end date — it stays in force until the songwriter revokes it.',
  },
]

/**
 * Nothing here ends a licence the same day.
 *
 * There used to be an `end_immediately` option on both of these. It was
 * removed deliberately: a membership row changing is somebody else's admin
 * action, and letting it cut a church off mid-rotation — with music printed
 * and a service already rehearsed — punishes the church for the writer's life
 * admin. It was also unrecoverable, so one mis-click destroyed a real
 * agreement.
 *
 * A writer who genuinely needs a song out fast does it deliberately, through
 * withdrawal, not as a side effect of leaving. Every outcome below either
 * changes nothing or winds down, and every wind-down can be reversed while it
 * is still running.
 */
export const ON_LEAVE_CHURCH = [
  {
    id: 'continue_to_term',
    label: 'Keeps going until it was due to end',
    blurb: 'If you leave the church, the licence runs out its term as normal rather than stopping early.',
  },
  {
    id: 'wind_down',
    label: 'Winds down over a set number of days',
    blurb: 'If you leave the church, the licence keeps working for a set number of days so the church can retire the song properly, then ends. If you come back before it finishes, it simply carries on.',
  },
]

export const ON_LEAVE_WORSHIP_TEAM = [
  {
    id: 'continue_to_term',
    label: 'Keeps going until it was due to end',
    blurb: 'If you stay at the church but come off the worship team, nothing changes — the licence runs its normal term.',
  },
  {
    id: 'wind_down',
    label: 'Winds down over a set number of days',
    blurb: 'If you stay at the church but come off the worship team, the licence keeps working for a set number of days, then ends. If you rejoin the team before it finishes, it simply carries on.',
  },
]

export const ON_THIRD_PARTY_PUBLISH = [
  {
    id: 'survives',
    label: 'This licence keeps going',
    blurb: 'If the song later gets registered with CCLI, APRA, or another publisher, this church’s licence is unaffected.',
  },
  {
    id: 'renegotiate',
    label: 'Both sides revisit the terms',
    blurb: 'If the song later gets registered with CCLI, APRA, or another publisher, you and the church agree new terms rather than assume the old ones still fit.',
  },
  {
    id: 'lapses',
    label: 'This licence ends',
    blurb: 'If the song later gets registered with CCLI, APRA, or another publisher, this direct licence ends and the church uses it through that publisher instead.',
  },
]

export const RENEWAL = [
  {
    id: 'expire',
    label: 'Just expires',
    blurb: 'When the term is up, the licence ends — both of you are notified a week out, and nobody has to do anything to let it lapse.',
  },
  {
    id: 'require_reapproval',
    label: 'Needs re-approval',
    blurb: 'When the term is up, you’re asked to approve it again before the church can keep using the song — the church is notified once you decide.',
  },
  {
    id: 'auto_renew',
    label: 'Auto-renews',
    blurb: 'When the term is up, it renews on the same terms automatically — you’re both notified it happened, in case either of you wants to change something.',
  },
]

const addMonths = (date, months) => {
  const d = new Date(date)
  d.setMonth(d.getMonth() + months)
  return d
}

/** Fixed-term end date, or null when the licence is indefinite-until-revoked. */
export const computeExpiry = (startsAt, durationMonths) => {
  if (!startsAt || !durationMonths) return null
  return addMonths(startsAt, durationMonths)
}

/** Days left until expiry, or null when there is no expiry to count down. */
export const daysUntilExpiry = (row) => {
  if (!row?.expires_at) return null
  const ms = new Date(row.expires_at).getTime() - Date.now()
  return Math.ceil(ms / (1000 * 60 * 60 * 24))
}

const shortDate = (value) =>
  new Date(value).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })

// Shared sort order for both licence dashboards (church-side Endorsements
// and songwriter-side Licences): rows still needing a decision or heading
// toward a renewal float to the top, settled/closed rows sink. Both screens
// describe the same agreement from opposite sides, so they must agree on
// what "needs attention" means — hence this lives here, not duplicated
// per screen.
export const urgencyKey = (row) => {
  if (row.status === 'proposed') return [0, 0]
  const days = daysUntilExpiry(row)
  if (days !== null && ['active', 'wind_down'].includes(row.status)) return [1, days]
  if (row.status === 'active') return [2, 0] // indefinite, no expiry
  return [3, 0] // expired / declined / revoked
}

/** Sorts rows most-urgent first, using urgencyKey. */
export const sortByUrgency = (rows) =>
  [...rows].sort((a, b) => {
    const [ga, da] = urgencyKey(a)
    const [gb, db] = urgencyKey(b)
    return ga !== gb ? ga - gb : da - db
  })

/** Short human string for a list row or badge, e.g. "Active · renews 12 Mar". */
export const licenceStatusLabel = (row) => {
  if (!row) return ''
  switch (row.status) {
    case 'proposed':
      return 'Waiting on a decision'
    case 'declined':
      return 'Declined'
    case 'revoked':
      return row.revoked_at ? `Revoked ${shortDate(row.revoked_at)}` : 'Revoked'
    case 'expired':
      return row.expires_at ? `Expired ${shortDate(row.expires_at)}` : 'Expired'
    case 'wind_down':
      return row.expires_at ? `Winding down · ends ${shortDate(row.expires_at)}` : 'Winding down'
    case 'active': {
      if (!row.expires_at) return 'Active · indefinite'
      const renewalEntry = RENEWAL.find((r) => r.id === row.renewal)
      const verb = renewalEntry?.id === 'auto_renew' ? 'renews' : 'ends'
      return `Active · ${verb} ${shortDate(row.expires_at)}`
    }
    default:
      return row.status
  }
}

// Deterministic (key-sorted) JSON stringify, so the same term values always
// hash to the same string regardless of property insertion order.
const canonicalStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map(canonicalStringify).join(',')}]`
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort()
      .map((k) => `${JSON.stringify(k)}:${canonicalStringify(value[k])}`)
      .join(',')}}`
  }
  return JSON.stringify(value)
}

/**
 * The term values a church is agreeing to, frozen at the moment of
 * acceptance — mirrors the snapshot/hash pattern song_publisher_documents
 * uses (migration 33), so a later change to this vocabulary can't quietly
 * reinterpret a licence someone already agreed to.
 */
export const buildTermsSnapshot = (row, acceptedBy, startsAt, expiresAt) => ({
  song_id: row.song_id,
  church_id: row.church_id,
  songwriter_id: row.songwriter_id,
  duration_months: row.duration_months ?? null,
  scope_rehearsal: !!row.scope_rehearsal,
  scope_live: !!row.scope_live,
  scope_recording: !!row.scope_recording,
  scope_streaming: !!row.scope_streaming,
  scope_print: !!row.scope_print,
  on_leave_church: row.on_leave_church,
  on_leave_worship_team: row.on_leave_worship_team,
  wind_down_days: row.wind_down_days ?? null,
  on_third_party_publish: row.on_third_party_publish,
  renewal: row.renewal,
  accepted_by: acceptedBy,
  starts_at: startsAt.toISOString(),
  expires_at: expiresAt ? expiresAt.toISOString() : null,
})

/** SHA-256 hex digest of the canonical (key-sorted) JSON of a terms snapshot. */
export const hashTerms = async (snapshot) => {
  const bytes = new TextEncoder().encode(canonicalStringify(snapshot))
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** Read-back sentences describing the agreed terms, for either portal. */
export const summarise = (row) => {
  if (!row) return []
  const lines = []

  const activeScopes = SCOPES.filter((s) => row[s.column]).map((s) => s.label)
  lines.push(
    activeScopes.length
      ? `Covers: ${activeScopes.join(', ')}.`
      : 'No uses have been agreed yet.'
  )

  if (!row.duration_months) {
    lines.push('Runs indefinitely, until the songwriter revokes it.')
  } else if (row.expires_at) {
    lines.push(`Runs for ${row.duration_months} months, until ${shortDate(row.expires_at)}.`)
  } else {
    lines.push(`Runs for ${row.duration_months} months once it starts.`)
  }

  const leaveChurch = ON_LEAVE_CHURCH.find((o) => o.id === row.on_leave_church)
  if (leaveChurch) {
    lines.push(
      row.on_leave_church === 'wind_down' && row.wind_down_days
        ? `If the songwriter leaves the church, it winds down over ${row.wind_down_days} days.`
        : leaveChurch.blurb
    )
  }

  const leaveTeam = ON_LEAVE_WORSHIP_TEAM.find((o) => o.id === row.on_leave_worship_team)
  if (leaveTeam) {
    lines.push(
      row.on_leave_worship_team === 'wind_down' && row.wind_down_days
        ? `If the songwriter leaves the worship team but stays at the church, it winds down over ${row.wind_down_days} days.`
        : leaveTeam.blurb
    )
  }

  const thirdParty = ON_THIRD_PARTY_PUBLISH.find((o) => o.id === row.on_third_party_publish)
  if (thirdParty) lines.push(thirdParty.blurb)

  const renewal = RENEWAL.find((o) => o.id === row.renewal)
  if (renewal) lines.push(renewal.blurb)

  return lines
}
